import React from "react";
import {
  Modal,
  Button,
  TextField,
  Dropdown,
  TextArea,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import axios from "axios";
import "./IncidentFormModal.scss";
import { useAppSelector } from "../../../../../redux";
import { title } from "process";

const technologyOptions = [
  { label: "Google Wallet", value: "Google Wallet" },
  { label: "Apple Wallet", value: "Apple Wallet" },
  { label: "Origo Portal", value: "Origo Portal" },
  { label: "Other", value: "Other" },
];

const componentOptions = [
  { label: "SDK", value: "SDK" },
  { label: "APK", value: "APK" },
  { label: "Authentication", value: "Authentication" },
  { label: "Authorization", value: "Authorization" },
  { label: "UI Components", value: "UI Components" },
  { label: "API Integration", value: "API Integration" },
  { label: "Database", value: "Database" },
  { label: "Reporting", value: "Reporting" },
  { label: "Other", value: "Other" },
];

const issueTypeOptions = [
  { label: "Bug", value: "Bug" },
  { label: "Feature Request", value: "Feature Request" },
  { label: "Enhancement", value: "Enhancement" },
  { label: "Task", value: "Task" },
  { label: "Technical Debt", value: "Technical Debt" },
];

const priorityOptions = [
  { label: "P1", value: "P1" },
  { label: "P2", value: "P2" },
  { label: "P3", value: "P3" },
];

const environmentOptions = [
  { label: "Production", value: "Production" },
  {
    label: "Partner Integration Environment",
    value: "Partner Integration Environment",
  },
  { label: "Development", value: "Development" },
  { label: "UAT", value: "UAT" },
];

interface IncidentFormData {
  title: string;
  technology: string;
  component: string;
  issueType: string;
  priority: string;
  environment: string;
  sdkAiVersion: string;
  subject: string;
  description: string;
  reportedDate: string;
  reportedBy: string;
}

const IncidentFormModal: React.FC<{
  incident?: any;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: "Add" | "Update" | "Delete";
}> = ({ incident, setModalOpen, mode }) => {
  // Get logged-in user details from Redux
  const { loggedUserDetail } = useAppSelector((state) => state.auth);

  // Get user's full name
  const loggedUserName = loggedUserDetail
    ? `${loggedUserDetail.first_name || ""} ${
        loggedUserDetail.last_name || ""
      }`.trim()
    : "";

  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [formData, setFormData] = React.useState<IncidentFormData>({
    title: "",
    technology: "",
    component: "",
    issueType: "",
    priority: "",
    environment: "",
    sdkAiVersion: "",
    subject: "",
    description: "",
    reportedDate: new Date().toISOString().split("T")[0],
    reportedBy: loggedUserName,
  });

  const [selectedDropdowns, setSelectedDropdowns] = React.useState<any>({
    technology: null,
    component: null,
    issueType: null,
    priority: null,
    environment: null,
  });

  React.useEffect(() => {
    if (incident && mode === "Update") {
      setFormData({ ...incident });
      // Set selected dropdowns based on incident data
      setSelectedDropdowns({
        technology: technologyOptions.find(
          (opt) => opt.value === incident.technology
        ),
        component: componentOptions.find(
          (opt) => opt.value === incident.component
        ),
        issueType: issueTypeOptions.find(
          (opt) => opt.value === incident.issueType
        ),
        priority: priorityOptions.find(
          (opt) => opt.value === incident.priority
        ),
        environment: environmentOptions.find(
          (opt) => opt.value === incident.environment
        ),
      });
    }
  }, [incident, mode]);

  const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [event.currentTarget.name]: event.currentTarget.value,
    });
  };

  const handleDropdownSelect =
    (field: string) => (data: { label: string; value: string }) => {
      setSelectedDropdowns({
        ...selectedDropdowns,
        [field]: data,
      });
      setFormData({
        ...formData,
        [field]: data ? data.value : "",
      });
    };

  const isFormValid = () => {
    return (
      // formData.title.trim() &&
      formData.subject.trim() &&
      formData.description.trim() &&
      formData.technology &&
      formData.component &&
      formData.issueType &&
      formData.priority &&
      formData.reportedBy.trim()
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setErrorMessage("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      if (mode === "Add") {
        // Generate unique numeric incident ID (smaller range to fit INT column)
        // Using last 6 digits of timestamp + 3 random digits
        const timestamp = Date.now();
        const shortTimestamp = parseInt(timestamp.toString().slice(-6)); // Last 6 digits
        const randomNum = Math.floor(Math.random() * 1000); // 3-digit random number
        const incidentID = parseInt(`${shortTimestamp}${randomNum}`);

        // Format date to MySQL datetime format (YYYY-MM-DD HH:mm:ss.S)
        const now = new Date();
        const formattedDate = `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
          now.getHours()
        ).padStart(2, "0")}:${String(now.getMinutes()).padStart(
          2,
          "0"
        )}:${String(now.getSeconds()).padStart(2, "0")}.${Math.floor(
          now.getMilliseconds() / 100
        )}`;

        console.log("🆔 Generated unique incident ID:", incidentID);

        // Prepare API payload matching backend schema
        const apiPayload = {
          incidentID: incidentID,
          // title: formData.title,
          title:'',
          description: formData.description,
          issueType: formData.issueType,
          category: "", // Empty as per requirement
          source: "", // Empty as per requirement
          priority: formData.priority,
          owningTeam: "", // Empty as per requirement
          assignedTo: "", // Empty as per requirement
          status: "Open", // Default status
          // reportedBy: formData.reportedBy,
          // reportedDate: formattedDate,
          // updatedBy: "Admin", // You can update this with actual admin name/id
          partnerID: loggedUserDetail?.ID?.toString() || "",
          partnerName: loggedUserName || "",
          technology: formData.technology,
          component: formData.component,
          environment: formData.environment || "",
          sdkApiVersion: formData.sdkAiVersion || "",
          subject: formData.subject,
          jiraTicketNum: 0,
          helixTicketNum: 0,
          salesForceNum: 0,
          slackIssueNum: 0,
          updatedBy: loggedUserName,
        };

        console.log("📤 Creating new incident with payload:", apiPayload);

        // Call POST API
        const response = await axios.post(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/incidents`,
          apiPayload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        console.log("✅ Incident created successfully:", response.data);

        // Show success modal
        setShowSuccessModal(true);

        // Also save to localStorage for local display
        const existingIncidents = localStorage.getItem("incidents");
        let incidents = [];
        if (existingIncidents) {
          try {
            incidents = JSON.parse(existingIncidents);
            if (!Array.isArray(incidents)) {
              incidents = [];
            }
          } catch (error) {
            console.error("Error parsing existing incidents:", error);
            incidents = [];
          }
        }

        const newIncident = {
          ...formData,
          id: incidentID,
          status: "Open",
        };
        incidents.push(newIncident);
        localStorage.setItem("incidents", JSON.stringify(incidents));

        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event("incidentsUpdated"));

        setIsSubmitting(false);
      } else if (mode === "Update" && incident) {
        // Format date to MySQL datetime format (YYYY-MM-DD HH:mm:ss.S)
        const now = new Date();
        const formattedDate = `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
          now.getHours()
        ).padStart(2, "0")}:${String(now.getMinutes()).padStart(
          2,
          "0"
        )}:${String(now.getSeconds()).padStart(2, "0")}.${Math.floor(
          now.getMilliseconds() / 100
        )}`;

        // Prepare API payload for update
        const updatePayload = {
          title: formData.title,
          description: formData.description,
          issueType: formData.issueType,
          category: "",
          source: "",
          priority: formData.priority,
          owningTeam: "",
          assignedTo: "",
          status: "Open",
          // updatedBy: "Admin",
          updatedDate: formattedDate,
          partnerID: loggedUserDetail?.ID?.toString() || "",
          partnerName: loggedUserName || "",
          technology: formData.technology,
          component: formData.component,
          environment: formData.environment || "",
          sdkApiVersion: formData.sdkAiVersion || "",
          subject: formData.subject,
          jiraTicketNum: 0,
          helixTicketNum: 0,
          salesForceNum: 0,
          slackIssueNum: 0,
          updatedBy: loggedUserName,
        };

        console.log(
          `📤 Updating incident ID ${incident.id} with payload:`,
          updatePayload
        );

        // Call PUT API
        const response = await axios.put(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/incidents/${incident.id}`,
          updatePayload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        console.log("✅ Incident updated successfully:", response.data);

        // Show success modal
        setShowSuccessModal(true);

        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event("incidentsUpdated"));
        setIsSubmitting(false);
      }
    } catch (error: any) {
      console.error("❌ Error saving incident:", error);
      setIsSubmitting(false);
      if (error.response) {
        console.error("Response error:", error.response.data);
        setErrorMessage(
          error.response.data.message ||
            error.response.statusText ||
            "Error saving incident"
        );
      } else if (error.request) {
        setErrorMessage("No response from server");
      } else {
        setErrorMessage(error.message || "Error saving incident");
      }
    }
  };

  const handleDelete = async () => {
    if (!incident) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      console.log(`🗑️ Deleting incident ID ${incident.id}`);

      // Call DELETE API
      const response = await axios.delete(
        `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/incidents/${incident.id}`
      );

      console.log("✅ Incident deleted successfully:", response.data);

      // Show success modal
      setShowSuccessModal(true);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event("incidentsUpdated"));

      setIsSubmitting(false);
    } catch (error: any) {
      console.error("❌ Error deleting incident:", error);
      setIsSubmitting(false);
      if (error.response) {
        console.error("Response error:", error.response.data);
        setErrorMessage(
          error.response.data.message ||
            error.response.statusText ||
            "Error deleting incident"
        );
      } else if (error.request) {
        setErrorMessage("No response from server");
      } else {
        setErrorMessage(error.message || "Error deleting incident");
      }
    }
  };

  return (
    <Modal
      headerText={
        showSuccessModal || errorMessage
          ? ""
          : mode === "Add"
          ? "Create New Incident"
          : mode === "Update"
          ? "Update Incident"
          : "Delete Incident"
      }
      width={showSuccessModal || errorMessage ? 500 : 800}
      onClose={() => setModalOpen(false)}
      className="incident-form-modal"
    >
      <div
        className={`incident-form-container ${
          showSuccessModal || errorMessage ? "center-content" : ""
        }`}
      >
        {showSuccessModal ? (
          <div className="status-message-container">
            <Iconography
              icon="circleCheck"
              size={IconographySizes.XLarge}
              theme={IconographyThemes.Success}
            />
            <div className="message">
              {mode === "Add"
                ? "Incident Created Successfully"
                : mode === "Update"
                ? "Incident Updated Successfully"
                : "Incident Deleted Successfully"}
            </div>
          </div>
        ) : errorMessage ? (
          <div className="status-message-container">
            <Iconography
              icon="circleXmark"
              size={IconographySizes.XLarge}
              theme={IconographyThemes.Error}
            />
            <div className="message">{errorMessage}</div>
          </div>
        ) : mode === "Delete" ? (
          <div className="delete-confirmation">
            <div className="warning-icon">
              <Iconography
                icon="triangleExclamation"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Warning}
              />
            </div>
            <div className="confirmation-message">
              <h3>Delete Incident</h3>
              <p className="warning-text">
                Are you sure you want to permanently delete this incident? This
                action cannot be undone.
              </p>
            </div>
            <div className="incident-details-card">
              <div className="detail-row">
                <span className="detail-label">Incident Title:</span>
                <span className="detail-value">{incident?.title}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Subject:</span>
                <span className="detail-value">{incident?.subject}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Technology:</span>
                <span className="detail-value">{incident?.technology}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Priority:</span>
                <span className="detail-value priority-badge-{incident?.priority?.toLowerCase()}">
                  {incident?.priority}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="details-form">
            {/* Row 1: Title */}
            {/* <div className="input-row-wrapper">
              <div className="label-field-container">
                <label className="field-label">
                  Title<span>*</span>
                </label>
                <TextField
                  id="title"
                  name="title"
                  placeholder="Enter incident title"
                  value={formData.title}
                  onChange={handleChange}
                />
              </div>
            </div> */}

            {/* Row 2: Technology, Component */}
            <div className="input-row-wrapper">
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">
                    Technology<span>*</span>
                  </label>
                  <Dropdown
                    id="technology"
                    name="technology"
                    placeholder="Select technology"
                    options={technologyOptions}
                    selected={selectedDropdowns.technology}
                    onSelect={handleDropdownSelect("technology")}
                  />
                </div>
              </div>
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">
                    Component<span>*</span>
                  </label>
                  <Dropdown
                    id="component"
                    name="component"
                    placeholder="Select component"
                    options={componentOptions}
                    selected={selectedDropdowns.component}
                    onSelect={handleDropdownSelect("component")}
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Issue Type, Priority */}
            <div className="input-row-wrapper">
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">
                    Issue Type<span>*</span>
                  </label>
                  <Dropdown
                    id="issueType"
                    name="issueType"
                    placeholder="Select issue type"
                    options={issueTypeOptions}
                    selected={selectedDropdowns.issueType}
                    onSelect={handleDropdownSelect("issueType")}
                  />
                </div>
              </div>
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">
                    Priority<span>*</span>
                  </label>
                  <Dropdown
                    id="priority"
                    name="priority"
                    placeholder="Select priority"
                    options={priorityOptions}
                    selected={selectedDropdowns.priority}
                    onSelect={handleDropdownSelect("priority")}
                  />
                </div>
              </div>
            </div>

            {/* Row 4: Environment, SDK/AI Version */}
            <div className="input-row-wrapper">
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">Environment</label>
                  <Dropdown
                    id="environment"
                    name="environment"
                    placeholder="Select environment"
                    options={environmentOptions}
                    selected={selectedDropdowns.environment}
                    onSelect={handleDropdownSelect("environment")}
                  />
                </div>
              </div>
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">SDK/AI Version</label>
                  <TextField
                    id="sdkAiVersion"
                    name="sdkAiVersion"
                    placeholder="Enter SDK/AI version"
                    value={formData.sdkAiVersion}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Row 5: Subject */}
            <div className="input-row-wrapper">
              <div className="label-field-container">
                <label className="field-label">
                  Subject<span>*</span>
                </label>
                <TextField
                  id="subject"
                  name="subject"
                  placeholder="Enter incident subject"
                  value={formData.subject}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Row 6: Description */}
            <div className="input-row-wrapper">
              <div className="label-field-container">
                <label className="field-label">
                  Description<span>*</span>
                </label>
                <TextArea
                  label=""
                  id="description"
                  name="description"
                  placeholder="Enter detailed description"
                  value={formData.description}
                  onChange={(e: any) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={4}
                />
              </div>
            </div>

            {/* Row 7: Reported Date, Reported By */}
            {/* <div className="input-row-wrapper">
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">Reported Date</label>
                  <TextField
                    id="reportedDate"
                    name="reportedDate"
                    type="date"
                    value={formData.reportedDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="input-col-wrapper">
                <div className="label-field-container">
                  <label className="field-label">
                    Reported By<span>*</span>
                  </label>
                  <TextField
                    id="reportedBy"
                    name="reportedBy"
                    placeholder="Enter reporter name"
                    value={formData.reportedBy}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div> */}
          </div>
        )}

        {!showSuccessModal && !errorMessage && (
          <div className="button-group">
            <Button
              variant="secondary"
              label="Cancel"
              onClick={() => setModalOpen(false)}
              disabled={isSubmitting}
            />
            {mode === "Delete" ? (
              <Button
                variant="primary"
                label={isSubmitting ? "Deleting..." : "Delete"}
                onClick={handleDelete}
                disabled={isSubmitting}
              />
            ) : (
              <Button
                variant="primary"
                label={
                  isSubmitting
                    ? mode === "Add"
                      ? "Creating..."
                      : "Updating..."
                    : mode === "Add"
                    ? "Create Incident"
                    : "Update Incident"
                }
                disabled={!isFormValid() || isSubmitting}
                onClick={handleSubmit}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default IncidentFormModal;
